import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { saveTableQR, updateTableQR, deleteTableQR, getAllTables, TableQR } from '../utils/database';
import { Pencil, Trash2, Save, Download, Loader2 } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { app } from '../config/firebase';

// NOTE: AdminLogin.tsx (client gate) and database.rules.json (server gate) both
// list these same 4 accounts. Keep the three lists in sync.
// Comparison must be case-insensitive (Gmail addresses are case-insensitive).
const ADMIN_EMAILS = [
  'yacoub.faik@gmail.com',
  'abdelmoughitezinebi@gmail.com',
  'upsicast@gmail.com',
  'mehdidehri2012@gmail.com'
];

const isAdminEmail = (email: string | null): boolean =>
  !!email && ADMIN_EMAILS.includes(email.trim().toLowerCase());

const auth = getAuth(app);

interface QRCodeGeneratorProps {
  onQRGenerated?: () => void;
}

const QRCodeGenerator: React.FC<QRCodeGeneratorProps> = ({ onQRGenerated }) => {
  const [tableNumber, setTableNumber] = useState('1');
  const [tableName, setTableName] = useState('');
  const [savedTables, setSavedTables] = useState<TableQR[]>([]);
  const [selectedTable, setSelectedTable] = useState<TableQR | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const baseUrl = window.location.origin;

  const loadTables = React.useCallback(async () => {
    try {
      setLoading(true);
      const tables = await getAllTables();
      setSavedTables(tables);
    } catch (err) {
      console.error('Error loading tables:', err);
      setError('Failed to load tables');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        setError('You must be authenticated to manage tables');
        return;
      }
      
      if (!isAdminEmail(user.email)) {
        setError('You must be an admin to manage tables');
        return;
      }
      
      // Clear any previous errors if user is authenticated and admin
      setError(null);
      loadTables();
    });

    return () => unsubscribe();
  }, [loadTables]);

  const generateTableUrl = (tableNum: string): string => {
    const params = new URLSearchParams({
      table: tableNum,
      name: tableName,
      mode: 'order'
    });
    return `${baseUrl}?${params.toString()}`;
  };

  const handleSave = async () => {
    try {
      if (!auth.currentUser) {
        setError('You must be authenticated to perform this action');
        return;
      }

      if (!isAdminEmail(auth.currentUser.email)) {
        setError('You must be an admin to perform this action');
        return;
      }

      setLoading(true);
      setError(null);
      
      if (!tableNumber.trim() || !tableName.trim()) {
        setError('Table number and name are required');
        return;
      }

      if (!isEditing && savedTables.some(t => t.tableNumber === tableNumber)) {
        setError('Table number already exists');
        return;
      }

      if (isEditing && selectedTable?.id) {
        await updateTableQR(selectedTable.id, {
          tableName,
          tableNumber
        });
      } else {
        await saveTableQR({
          tableNumber,
          tableName: tableName || `Table ${tableNumber}`
        });
      }
      await loadTables();
      resetForm();
      onQRGenerated?.();
    } catch (error) {
      console.error('Error saving table:', error);
      setError('Failed to save table');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setSelectedTable(null);
    setTableNumber('');
    setTableName('');
    setError(null);
  };

  const handleEdit = (table: TableQR) => {
    setSelectedTable(table);
    setTableNumber(table.tableNumber);
    setTableName(table.tableName);
    setIsEditing(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this table?')) {
      try {
        setLoading(true);
        await deleteTableQR(id);
        await loadTables();
      } catch (error) {
        console.error('Error deleting table:', error);
        setError('Failed to delete table');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDownload = (table: TableQR): void => {
    const svg = document.querySelector(`#qr-${table.tableNumber} svg`);
    if (svg) {
      const scale = 8;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const data = (new XMLSerializer()).serializeToString(svg);
      
      // Create a base64 SVG
      const svgBase64 = btoa(data);
      const img = new Image();
      
      img.onload = () => {
        if (ctx) {
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          ctx.scale(scale, scale);
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          
          // Convert to data URL directly
          const pngUrl = canvas.toDataURL('image/png', 1.0);
          const link = document.createElement('a');
          link.download = `DJOCO-Table-${table.tableNumber}-${table.tableName.replace(/\s+/g, '-')}.png`;
          link.href = pngUrl;
          link.click();
        }
      };
      
      // Use data URL instead of blob
      img.src = `data:image/svg+xml;base64,${svgBase64}`;
    }
  };

  const generateQRCode = (table: TableQR) => {
    const url = `${baseUrl}?table=${encodeURIComponent(table.tableNumber)}&name=${encodeURIComponent(table.tableName)}`;
    return (
      <div id={`qr-${table.tableNumber}`} className="mb-3">
        <QRCodeSVG
          value={url}
          size={160}
          level="H"
          marginSize={4}
          className="mx-auto"
          bgColor="#FFFFFF"
          fgColor="#000000"
        />
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {error && (
        <div role="alert" className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        <div className="space-y-3">
          <div>
            <label className="block text-secondary font-heading text-sm font-semibold mb-1">
              Numéro de Table
            </label>
            <input
              type="number"
              min="1"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              className="w-full p-2.5 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400 bg-white text-sm"
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-secondary font-heading text-sm font-semibold mb-1">
              Nom de la Table
            </label>
            <input
              type="text"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              className="w-full p-2.5 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400 bg-white text-sm"
              disabled={loading}
            />
          </div>
          <button
            onClick={handleSave}
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white py-2.5 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm shadow-sm"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <>
                <Save size={16} />
                {isEditing ? 'Update Table' : 'Save Table'}
              </>
            )}
          </button>
        </div>

        {/* Preview QR code for current input */}
        {tableNumber && tableName && (
          <div className="bg-white p-4 rounded-2xl border border-stone-200/70 shadow-sm">
            <h3 className="text-base font-semibold mb-3 text-center text-stone-800">QR Code Preview</h3>
            <QRCodeSVG
              value={generateTableUrl(tableNumber)}
              size={176}
              level="H"
              marginSize={4}
              className="mx-auto"
            />
          </div>
        )}
      </div>

      <div className="mt-5">
        <h3 className="text-base font-semibold mb-3 text-secondary font-heading">Tables Enregistrées</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {savedTables.map((table) => (
            <div 
              key={table.id}
              className="bg-white p-4 rounded-2xl border border-stone-200/70 shadow-sm space-y-3"
            >
              <div className="flex justify-between items-start">
                <div className="min-w-0">
                  <h4 className="font-bold text-stone-800 text-sm">{table.tableName}</h4>
                  <p className="text-xs text-stone-500">Table #{table.tableNumber}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(table)}
                    className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                    title="Modifier"
                    aria-label={`Modifier ${table.tableName}`}
                  >
                    <Pencil size={15} aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => handleDelete(table.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                    aria-label={`Supprimer ${table.tableName}`}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                  </button>
                </div>
              </div>
              {generateQRCode(table)}
              <button
                onClick={() => handleDownload(table)}
                className="w-full flex items-center justify-center gap-1.5 p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors text-sm"
              >
                <Download size={15} />
                Télécharger QR Code
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator; 